import i18n from '../i18n/config';
import React, { useState, useEffect, useCallback, useRef } from 'react';
import { useLocation } from 'react-router-dom';
import {
  Container,
  Paper,
  Typography,
  Box,
  Grid,
  TextField,
  Button,
  Avatar,
  Divider,
  Card,
  CardContent,
  Alert,
  Tab,
  Tabs,
  IconButton,
  Autocomplete,
  CircularProgress,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Stepper,
  Step,
  StepLabel,
  Chip,
  FormControl,
  FormControlLabel,
  FormGroup,
  Switch,
  Collapse,
  List,
  ListItem,
  ListItemIcon,
  ListItemText,
  ListItemSecondaryAction,
  MenuItem,
  Select,
  InputLabel,
  Fade,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  useMediaQuery,
  useTheme
} from '@mui/material';
import {
  Person as PersonIcon,
  Edit as EditIcon,
  Save as SaveIcon,
  Cancel as CancelIcon,
  Lock as LockIcon,
  Business as BusinessIcon,
  Email as EmailIcon,
  AccessTime as TimezoneIcon,
  Send as SendIcon,
  CheckCircle as CheckIcon,
  Warning as WarningIcon,
  VpnKey as KeyIcon,
  Settings as SettingsIcon,
  Notifications as NotificationsIcon,
  Speed as SpeedIcon,
  Thermostat as ThermostatIcon,
  TrendingUp as TrendingUpIcon,
  LocalShipping as TuneIcon,
  Science as ScienceIcon,
  Public as PublicIcon,
  Refresh as RefreshIcon,
  ExpandMore as ExpandMoreIcon,
  ExpandLess as ExpandLessIcon,
  Calculate as CalculateIcon
} from '@mui/icons-material';
import { useFormik } from 'formik';
import * as Yup from 'yup';

// Import i18n hooks
import { useTranslation } from 'react-i18next';
import { useLanguage } from '../hooks/useLanguage';
import { useTimezone } from '../hooks/useTimezone';

import { useAuth } from '../hooks/useAuth';
import { useApiCall, useMutation } from '../hooks/useApi';
import { api, unitPreferencesHelpers } from '../services/api';
import { setCachedUnitPreferences } from '../services/unitPreferencesStore';
import LoadingPage from '../components/common/LoadingPage';
import PhoneInput from '../components/common/PhoneInput';
import NotificationPreferences from '../components/settings/NotificationPreferences';
import AdoptionNotificationPreferences from '../components/settings/AdoptionNotificationPreferences';
import { refreshUserTimezone } from '../utils/formatters';

// Hook personalizado para debounce
const useDebounce = (value, delay) => {
  const { t } = useTranslation();
  const [debouncedValue, setDebouncedValue] = useState(value);

  useEffect(() => {
    const handler = setTimeout(() => {
      setDebouncedValue(value);
    }, delay);

    return () => {
      clearTimeout(handler);
    };
  }, [value, delay]);

  return debouncedValue;
};

const ProfilePage = () => {
  const location = useLocation();
  const { user, updateUser } = useAuth();
  const [tabValue, setTabValue] = useState(0);
  const [successMessage, setSuccessMessage] = useState('');

  // Estados para guardado automático
  const [autoSaveStatus, setAutoSaveStatus] = useState(''); // '', 'saving', 'saved', 'error'
  const [autoSaveMessage, setAutoSaveMessage] = useState('');

  // Password Reset Dialog States
  const [resetDialogOpen, setResetDialogOpen] = useState(false);
  const [resetStep, setResetStep] = useState(0);
  const [resetLoading, setResetLoading] = useState(false);
  const [resetError, setResetError] = useState('');
  const [resetUserInfo, setResetUserInfo] = useState(null);

  // Unit Preferences States
  const [showConversionTest, setShowConversionTest] = useState(false);
  const [conversionResult, setConversionResult] = useState(null);
  const [conversionError, setConversionError] = useState('');

  // Translation and language hooks
  const { t } = useTranslation();
  const { currentLanguage, changeLanguage, applyLanguageAndReload, getAvailableLanguages, isChangingLanguage } = useLanguage();

  // Responsive helpers
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down('sm'));

  // Timezone hook
  const {
    timezones,
    loading: timezonesLoading,
    error: timezonesError,
    getCurrentTimezone,
    validateTimezone
  } = useTimezone();

  // Fetch current user profile
  const {
    data: userProfile,
    loading: profileLoading,
    error: profileError,
    refetch: refetchProfile
  } = useApiCall(api.users.getProfile);

  // Fetch unit preferences
  const {
    data: unitPreferences,
    loading: unitPreferencesLoading,
    error: unitPreferencesError,
    refetch: refetchUnitPreferences
  } = useApiCall(api.unitPreferences.getMyPreferences);

  // Fetch available units
  const {
    data: availableUnitsData,
    loading: availableUnitsLoading
  } = useApiCall(api.unitPreferences.getAvailableUnits);

  // Fetch conversion examples
  const {
    data: conversionExamples,
    loading: conversionExamplesLoading
  } = useApiCall(api.unitPreferences.getConversionExamples);

  const normalizeProfilePayload = useCallback((values) => ({
    name: values?.name || '',
    last_name: values?.last_name || '',
    email: values?.email || '',
    phone: values?.phone || '',
    language: values?.language || 'es',
    timezone: values?.timezone || getCurrentTimezone() || 'America/Mexico_City',
  }), [getCurrentTimezone]);

  const getProfileSignature = useCallback((values) => {
    const payload = normalizeProfilePayload(values);
    return JSON.stringify(payload);
  }, [normalizeProfilePayload]);

  const normalizeUnitPayload = useCallback((values) => {
    const pressureDecimals = parseInt(
      values?.pressure_decimals
      ?? values?.decimal_places_map?.pressure
      ?? values?.display_settings?.pressure?.decimals
      ?? values?.decimal_places
      ?? 2,
      10
    );

    const temperatureDecimals = parseInt(
      values?.temperature_decimals
      ?? values?.decimal_places_map?.temperature
      ?? values?.display_settings?.temperature?.decimals
      ?? 1,
      10
    );

    const accelerationDecimals = parseInt(
      values?.acceleration_decimals
      ?? values?.decimal_places_map?.acceleration
      ?? values?.display_settings?.acceleration?.decimals
      ?? values?.decimal_places
      ?? 2,
      10
    );

    const speedDecimals = parseInt(
      values?.speed_decimals
      ?? values?.decimal_places_map?.speed
      ?? values?.display_settings?.speed?.decimals
      ?? values?.decimal_places
      ?? 2,
      10
    );

    const decimalPlacesMap = {
      pressure: Number.isInteger(pressureDecimals) ? pressureDecimals : 2,
      temperature: Number.isInteger(temperatureDecimals) ? temperatureDecimals : 1,
      acceleration: Number.isInteger(accelerationDecimals) ? accelerationDecimals : 2,
      speed: Number.isInteger(speedDecimals) ? speedDecimals : 2,
    };

    return {
      pressure_unit: values?.pressure_unit || 'PSI',
      temperature_unit: values?.temperature_unit || 'C',
      acceleration_unit: values?.acceleration_unit || 'G',
      speed_unit: values?.speed_unit || 'km/h',
      use_imperial_system: Boolean(values?.use_imperial_system),
      decimal_places: String(decimalPlacesMap.pressure),
      decimal_places_map: decimalPlacesMap,
    };
  }, []);

  const getUnitSignature = useCallback((values) => {
    const payload = normalizeUnitPayload(values);
    return JSON.stringify(payload);
  }, [normalizeUnitPayload]);

  const profileLastSavedSignatureRef = useRef('');
  const profileLastSubmittedSignatureRef = useRef('');
  const unitLastSavedSignatureRef = useRef('');
  const unitLastSubmittedSignatureRef = useRef('');

  // Update profile mutation with language change support
  const {
    mutate: updateProfile,
    loading: updateLoading,
    error: updateError
  } = useMutation(api.users.updateProfile, {
    onSuccess: async (data) => {
      const savedSignature = getProfileSignature(data || {});
      profileLastSavedSignatureRef.current = savedSignature;
      profileLastSubmittedSignatureRef.current = '';

      if (data.language && data.language !== currentLanguage) {
        await changeLanguage(data.language, false);
      }

      if (data.timezone && data.timezone !== userProfile?.timezone) {
        console.log('Timezone updated, refreshing timezone cache');
        refreshUserTimezone();
      }

      updateUser(data);
      setAutoSaveStatus('saved');
      setAutoSaveMessage(t('hardcoded.pages_profilepage.guardado'));
      setTimeout(() => {
        setAutoSaveStatus('');
        setAutoSaveMessage('');
      }, 2000);
      refetchProfile();
    },
    onError: () => {
      profileLastSubmittedSignatureRef.current = '';
      setAutoSaveStatus('error');
      setAutoSaveMessage(t('hardcoded.pages_admintpmsdefaultspage.error_al_guardar'));
      setTimeout(() => {
        setAutoSaveStatus('');
        setAutoSaveMessage('');
      }, 3000);
    }
  });

  // Update password mutation
  const {
    mutate: updatePassword,
    loading: passwordLoading,
    error: passwordError
  } = useMutation(api.users.updatePassword, {
    onSuccess: () => {
      passwordFormik.resetForm();
      setSuccessMessage(t('profile.passwordUpdatedSuccess'));
      setTimeout(() => setSuccessMessage(''), 3000);
    }
  });

  // Update unit preferences mutation
  const {
    mutate: updateUnitPreferences,
    loading: unitUpdateLoading,
    error: unitUpdateError
  } = useMutation(api.unitPreferences.updateMyPreferences, {
    onSuccess: (data) => {
      const savedSignature = getUnitSignature(data || {});
      unitLastSavedSignatureRef.current = savedSignature;
      unitLastSubmittedSignatureRef.current = '';

      setCachedUnitPreferences(data);
      setAutoSaveStatus('saved');
      setAutoSaveMessage(t('hardcoded.pages_profilepage.guardado'));
      setTimeout(() => {
        setAutoSaveStatus('');
        setAutoSaveMessage('');
      }, 2000);
      refetchUnitPreferences();
    },
    onError: () => {
      unitLastSubmittedSignatureRef.current = '';
      setAutoSaveStatus('error');
      setAutoSaveMessage(t('hardcoded.pages_admintpmsdefaultspage.error_al_guardar'));
      setTimeout(() => {
        setAutoSaveStatus('');
        setAutoSaveMessage('');
      }, 3000);
    }
  });

  // Apply preset mutation
  const {
    mutate: applyPreset,
    loading: presetLoading
  } = useMutation(api.unitPreferences.applyPreset, {
    onSuccess: () => {
      setSuccessMessage(t('hardcoded.pages_profilepage.preset_aplicado_exitosamente'));
      setTimeout(() => setSuccessMessage(''), 3000);
      refetchUnitPreferences();
    }
  });

  // Reset to defaults mutation
  const {
    mutate: resetUnitPreferences,
    loading: resetUnitsLoading
  } = useMutation(api.unitPreferences.resetToDefaults, {
    onSuccess: () => {
      setSuccessMessage(t('hardcoded.pages_profilepage.preferencias_de_unidades_restablecidas_a'));
      setTimeout(() => setSuccessMessage(''), 3000);
      refetchUnitPreferences();
    }
  });

  // Test conversion mutation
  const {
    mutate: testConversion,
    loading: conversionTestLoading
  } = useMutation(api.unitPreferences.testConversion, {
    onSuccess: (data) => {
      setConversionResult(data);
      setConversionError('');
    },
    onError: (error) => {
      setConversionError(error.response?.data?.detail || t('hardcoded.pages_profilepage.error_en_la_conversion'));
      setConversionResult(null);
    }
  });

  // Create validation schemas
  const profileValidationSchema = Yup.object({
    name: Yup.string().required(t('profile.personalInfo.validation.nameRequired')),
    last_name: Yup.string().required(t('profile.personalInfo.validation.lastNameRequired')),
    email: Yup.string()
      .email(t('profile.personalInfo.validation.invalidEmail'))
      .required(t('profile.personalInfo.validation.emailRequired')),
    phone: Yup.string(),
    language: Yup.string().required(t('profile.personalInfo.validation.languageRequired')),
    timezone: Yup.string()
      .required(t('profile.personalInfo.validation.timezoneRequired'))
      .test('valid-timezone', t('profile.personalInfo.validation.invalidTimezone'), (value) => {
        return value ? validateTimezone(value) : true;
      }),
  });

  const passwordValidationSchema = Yup.object({
    current_password: Yup.string().required(t('profile.security.validation.currentPasswordRequired')),
    new_password: Yup.string()
      .min(8, t('profile.security.validation.newPasswordMinLength'))
      .required(t('profile.security.validation.newPasswordRequired')),
    confirm_password: Yup.string()
      .oneOf([Yup.ref('new_password')], t('profile.security.validation.passwordsMustMatch'))
      .required(t('profile.security.validation.confirmPasswordRequired')),
  });

  // Unit preferences validation schema
  const unitPreferencesValidationSchema = Yup.object({
    pressure_unit: Yup.string().required(t('hardcoded.pages_profilepage.unidad_de_presion_es_requerida')),
    temperature_unit: Yup.string().required(t('hardcoded.pages_profilepage.unidad_de_temperatura_es_requerida')),
    acceleration_unit: Yup.string().required(t('hardcoded.pages_profilepage.unidad_de_aceleracion_es_requerida')),
    speed_unit: Yup.string().required(t('hardcoded.pages_profilepage.unidad_de_velocidad_es_requerida')),
    pressure_decimals: Yup.string().required(t('hardcoded.pages_profilepage.decimales_de_presion_requeridos')).test(t('hardcoded.pages_profilepage.valid-decimals-pressure'), t('hardcoded.pages_profilepage.debe_ser_entre_0_y'), (value) => unitPreferencesHelpers.validateDecimalPlaces(value)),
    temperature_decimals: Yup.string().required(t('hardcoded.pages_profilepage.decimales_de_temperatura_requeridos')).test(t('hardcoded.pages_profilepage.valid-decimals-temp'), t('hardcoded.pages_profilepage.debe_ser_entre_0_y'), (value) => unitPreferencesHelpers.validateDecimalPlaces(value)),
    acceleration_decimals: Yup.string().required(t('hardcoded.pages_profilepage.decimales_de_aceleracion_requeridos')).test(t('hardcoded.pages_profilepage.valid-decimals-acc'), t('hardcoded.pages_profilepage.debe_ser_entre_0_y'), (value) => unitPreferencesHelpers.validateDecimalPlaces(value)),
    speed_decimals: Yup.string().required(t('hardcoded.pages_profilepage.decimales_de_velocidad_requeridos')).test('valid-decimals-speed', t('hardcoded.pages_profilepage.debe_ser_entre_0_y'), (value) => unitPreferencesHelpers.validateDecimalPlaces(value)),
  });

  const resetValidationSchema = Yup.object({
    email: Yup.string()
      .email(t('profile.security.reset.validation.invalidEmail'))
      .required(t('profile.security.reset.validation.emailRequired')),
  });

  // Profile formik
  const profileFormik = useFormik({
    initialValues: {
      name: userProfile?.name || '',
      last_name: userProfile?.last_name || '',
      email: userProfile?.email || '',
      phone: userProfile?.phone || '',
      language: userProfile?.language || currentLanguage || 'es',
      timezone: userProfile?.timezone || getCurrentTimezone() || 'America/Mexico_City',
    },
    validationSchema: profileValidationSchema,
    onSubmit: (values) => {
      // No se usa directamente, el guardado es automático
    },
    enableReinitialize: true,
  });

  // Password formik
  const passwordFormik = useFormik({
    initialValues: {
      current_password: '',
      new_password: '',
      confirm_password: '',
    },
    validationSchema: passwordValidationSchema,
    onSubmit: (values) => {
      updatePassword({
        current_password: values.current_password,
        new_password: values.new_password,
      });
    },
  });

  // Unit preferences formik
  const unitFormik = useFormik({
    initialValues: {
      pressure_unit: unitPreferences?.pressure_unit || 'PSI',
      temperature_unit: unitPreferences?.temperature_unit || 'C',
      acceleration_unit: unitPreferences?.acceleration_unit || 'G',
      speed_unit: unitPreferences?.speed_unit || 'km/h',
      use_imperial_system: unitPreferences?.use_imperial_system || false,
      pressure_decimals: String(unitPreferences?.decimal_places_map?.pressure ?? unitPreferences?.display_settings?.pressure?.decimals ?? unitPreferences?.decimal_places ?? '2'),
      temperature_decimals: String(unitPreferences?.decimal_places_map?.temperature ?? unitPreferences?.display_settings?.temperature?.decimals ?? '1'),
      acceleration_decimals: String(unitPreferences?.decimal_places_map?.acceleration ?? unitPreferences?.display_settings?.acceleration?.decimals ?? unitPreferences?.decimal_places ?? '2'),
      speed_decimals: String(unitPreferences?.decimal_places_map?.speed ?? unitPreferences?.display_settings?.speed?.decimals ?? unitPreferences?.decimal_places ?? '2'),
    },
    validationSchema: unitPreferencesValidationSchema,
    onSubmit: (values) => {
      // No se usa directamente, el guardado es automático
    },
    enableReinitialize: true,
  });

  // Conversion test formik
  const conversionFormik = useFormik({
    initialValues: {
      value: 100,
      from_unit: 'PSI',
      to_unit: 'BAR',
      conversion_type: 'pressure',
    },
    onSubmit: (values) => {
      testConversion(values);
    },
  });

  const resetFormik = useFormik({
    initialValues: {
      email: userProfile?.email || '',
    },
    validationSchema: resetValidationSchema,
    onSubmit: async (values) => {
      await handleEmailValidation(values.email);
    },
    enableReinitialize: true,
  });

  // Debounce para valores del perfil
  const debouncedProfileValues = useDebounce(profileFormik.values, 800);

  // Debounce para valores de unidades
  const debouncedUnitValues = useDebounce(unitFormik.values, 800);

  // Estado para controlar si es la primera carga (evitar guardado automático inicial)
  const [isInitialLoadProfile, setIsInitialLoadProfile] = useState(true);
  const [isInitialLoadUnits, setIsInitialLoadUnits] = useState(true);

  // Efecto para guardado automático del perfil
  useEffect(() => {
    if (isInitialLoadProfile) {
      setIsInitialLoadProfile(false);
      return;
    }

    if (!profileFormik.dirty || updateLoading) {
      return;
    }

    const normalizedProfilePayload = normalizeProfilePayload(debouncedProfileValues);
    const profileSignature = JSON.stringify(normalizedProfilePayload);

    if (
      profileSignature === profileLastSavedSignatureRef.current ||
      profileSignature === profileLastSubmittedSignatureRef.current
    ) {
      return;
    }

    const saveProfile = async () => {
      // Validar antes de guardar
      try {
        await profileValidationSchema.validate(normalizedProfilePayload);

        profileLastSubmittedSignatureRef.current = profileSignature;

        // Mostrar "Guardando..."
        setAutoSaveStatus('saving');
        setAutoSaveMessage(t('sensorForm.saving'));

        // Guardar
        updateProfile(normalizedProfilePayload);
      } catch (error) {
        // Error de validación, no guardar
        console.log('Validation error, not saving:', error);
      }
    };

    if (!isChangingLanguage) {
      saveProfile();
    }
  }, [debouncedProfileValues, profileFormik.dirty, updateLoading, isChangingLanguage, normalizeProfilePayload, profileValidationSchema, updateProfile]);

  // Efecto para guardado automático de preferencias de unidades
  useEffect(() => {
    if (isInitialLoadUnits) {
      setIsInitialLoadUnits(false);
      return;
    }

    if (!unitFormik.dirty || unitUpdateLoading) {
      return;
    }

    const normalizedUnitPayload = normalizeUnitPayload(debouncedUnitValues);
    const unitSignature = JSON.stringify(normalizedUnitPayload);

    if (
      unitSignature === unitLastSavedSignatureRef.current ||
      unitSignature === unitLastSubmittedSignatureRef.current
    ) {
      return;
    }

    const saveUnitPreferences = async () => {
      // Validar antes de guardar
      try {
        await unitPreferencesValidationSchema.validate(debouncedUnitValues);

        unitLastSubmittedSignatureRef.current = unitSignature;

        // Mostrar "Guardando..."
        setAutoSaveStatus('saving');
        setAutoSaveMessage(t('sensorForm.saving'));

        // Guardar
        updateUnitPreferences(normalizedUnitPayload);
      } catch (error) {
        // Error de validación, no guardar
        console.log('Validation error, not saving:', error);
      }
    };

    saveUnitPreferences();
  }, [debouncedUnitValues, unitFormik.dirty, unitUpdateLoading, normalizeUnitPayload, unitPreferencesValidationSchema, updateUnitPreferences]);

  useEffect(() => {
    if (userProfile) {
      profileLastSavedSignatureRef.current = getProfileSignature(userProfile);
    }
  }, [userProfile, getProfileSignature]);

  useEffect(() => {
    if (unitPreferences) {
      unitLastSavedSignatureRef.current = getUnitSignature(unitPreferences);
    }
  }, [unitPreferences, getUnitSignature]);

  useEffect(() => {
    if (unitPreferences) {
      setCachedUnitPreferences(unitPreferences);
    }
  }, [unitPreferences]);

  const handleTabChange = (event, newValue) => {
    setTabValue(newValue);
  };

  useEffect(() => {
    const queryParams = new URLSearchParams(location.search);
    const requestedTab = queryParams.get(t('hardcoded.pages_profilepage.tab'));

    if (requestedTab === 'units') {
      setTabValue(2);
    }

    if (requestedTab === 'notifications') {
      setTabValue(3);
    }
  }, [location.search]);

  const handleLanguageChange = async (event) => {
    const newLanguage = event.target.value;
    if (!newLanguage || newLanguage === currentLanguage) return;
    // Persistir y recargar la página: evita el titileo del auto-guardado debounced
    // y garantiza que todas las traducciones se apliquen limpiamente.
    await applyLanguageAndReload(newLanguage, (code) =>
      api.users.updateProfile({ language: code })
    );
  };

  const handleTimezoneChange = (event, newValue) => {
    const timezone = newValue ? newValue.value : '';
    profileFormik.setFieldValue('timezone', timezone);
  };

  const getSelectedTimezone = () => {
    const currentValue = profileFormik.values.timezone;
    return timezones.find((tz) => (
      tz.value === currentValue ||
      tz.standardName === currentValue ||
      (tz.utcIdentifiers || []).includes(currentValue)
    )) || null;
  };

  // Handle preset application
  const handleApplyPreset = (presetName) => {
    applyPreset(presetName);
  };

  // Handle conversion test
  const handleConversionTest = () => {
    setShowConversionTest(!showConversionTest);
    if (!showConversionTest) {
      setConversionResult(null);
      setConversionError('');
    }
  };

  // Password Reset Dialog Functions
  const handleOpenResetDialog = () => {
    setResetDialogOpen(true);
    setResetStep(0);
    setResetError('');
    setResetUserInfo(null);
    resetFormik.resetForm();
  };

  const handleCloseResetDialog = () => {
    setResetDialogOpen(false);
    setResetStep(0);
    setResetError('');
    setResetUserInfo(null);
    setResetLoading(false);
    resetFormik.resetForm();
  };

  const handleEmailValidation = async (email) => {
    setResetLoading(true);
    setResetError('');

    try {
      const response = await api.auth.validateEmail({ email });

      if (response.data.success) {
        setResetUserInfo({
          email: email,
          name: response.data.user_name,
          companyCount: response.data.company_count
        });
        setResetStep(1);
      }
    } catch (err) {
      const errorMessage = err.response?.data?.detail || t('profile.security.reset.emailNotFound');
      setResetError(errorMessage);
    } finally {
      setResetLoading(false);
    }
  };

  const handlePasswordReset = async () => {
    setResetLoading(true);
    setResetError('');

    try {
      const response = await api.auth.requestPasswordReset({
        email: resetUserInfo.email
      });

      if (response.data.success && response.data.email_sent) {
        setResetStep(2);
      } else {
        setResetError(response.data.message || t('profile.security.reset.sendError'));
      }
    } catch (err) {
      const errorMessage = err.response?.data?.detail || t('profile.security.reset.sendError');
      setResetError(errorMessage);
    } finally {
      setResetLoading(false);
    }
  };

  // Get filtered unit options by type
  const getUnitOptions = (unitType) => {
    if (!availableUnitsData?.detailed_units) return [];

    const excludedUnits = {
      pressure: ['atm', 'hPa', 'mmHg', 'inHg'],
      temperature: ['R'],
      speed: ['knots', 'm/s'],
      acceleration: []
    };

    const units = availableUnitsData.detailed_units[unitType] || [];
    const excluded = excludedUnits[unitType] || [];

    return units.filter(unit => !excluded.includes(unit.code));
  };

  // Get preset configurations with details
  const getPresetConfigurations = () => {
    const basePresets = unitPreferencesHelpers.getPresetConfigurations();

    return {
      metric: {
        ...basePresets.metric,
        config: {
          pressure_unit: 'BAR',
          temperature_unit: 'C',
          acceleration_unit: 'G',
          speed_unit: 'km/h',
          decimal_places_map: {
            pressure: 2,
            temperature: 1,
            acceleration: 2,
            speed: 2,
          }
        }
      },
      imperial: {
        ...basePresets.imperial,
        config: {
          pressure_unit: 'PSI',
          temperature_unit: 'F',
          acceleration_unit: 'G',
          speed_unit: 'mph',
          decimal_places_map: {
            pressure: 2,
            temperature: 1,
            acceleration: 2,
            speed: 2,
          }
        }
      },
      scientific: {
        ...basePresets.scientific,
        config: {
          pressure_unit: 'PA',
          temperature_unit: 'K',
          acceleration_unit: 'MS2',
          speed_unit: 'MS',
          decimal_places_map: {
            pressure: 4,
            temperature: 2,
            acceleration: 4,
            speed: 4,
          }
        }
      }
    };
  };

  // Reset Dialog Content Renderers
  const renderResetEmailStep = () => (
    <Box>
      <Typography variant="body1" color="text.secondary" sx={{ mb: 3 }}>
        {t('profile.security.reset.emailStepDescription')}
      </Typography>

      {resetError && (
        <Alert severity="error" sx={{ mb: 3 }}>
          {resetError}
        </Alert>
      )}

      <Box component="form" onSubmit={resetFormik.handleSubmit}>
        <TextField
          fullWidth
          id="email"
          name="email"
          label={t('profile.security.reset.emailAddress')}
          type="email"
          value={resetFormik.values.email}
          onChange={resetFormik.handleChange}
          onBlur={resetFormik.handleBlur}
          error={resetFormik.touched.email && Boolean(resetFormik.errors.email)}
          helperText={resetFormik.touched.email && resetFormik.errors.email}
          InputProps={{
            startAdornment: <EmailIcon color="action" sx={{ mr: 1 }} />,
          }}
          sx={{ mb: 3 }}
          autoFocus
        />
      </Box>
    </Box>
  );

  const renderResetConfirmationStep = () => (
    <Box>
      <Box sx={{ textAlign: 'center', mb: 3 }}>
        <CheckIcon sx={{ fontSize: 48, color: 'success.main', mb: 2 }} />
        <Typography variant="h6" gutterBottom>
          {t('profile.security.reset.emailConfirmed')}
        </Typography>
      </Box>

      {resetError && (
        <Alert severity="error" sx={{ mb: 3 }}>
          {resetError}
        </Alert>
      )}

      <Paper sx={{ p: 2, bgcolor: 'grey.50', mb: 3 }}>
        <Typography variant="subtitle2" gutterBottom color="primary">
          {t('profile.security.reset.accountInfo')}
        </Typography>
        <Typography variant="body2" sx={{ mb: 1 }}>
          <strong>{t('profile.security.reset.name')}:</strong> {resetUserInfo?.name}
        </Typography>
        <Typography variant="body2" sx={{ mb: 1 }}>
          <strong>{t('profile.security.reset.email')}:</strong> {resetUserInfo?.email}
        </Typography>
        <Typography variant="body2">
          <strong>{t('profile.security.reset.companies')}:</strong> {resetUserInfo?.companyCount} {t('profile.security.reset.companiesAssociated')}
        </Typography>
      </Paper>

      <Alert severity="info" sx={{ mb: 3 }}>
        <Typography variant="body2">
          <strong>{t('profile.security.reset.whatHappens')}</strong><br />
          • {t('profile.security.reset.tempPasswordGenerated')}<br />
          • {t('profile.security.reset.emailInMinutes')}<br />
          • {t('profile.security.reset.loginImmediately')}<br />
          • {t('profile.security.reset.recommendChange')}
        </Typography>
      </Alert>
    </Box>
  );

  const renderResetSuccessStep = () => (
    <Box sx={{ textAlign: 'center' }}>
      <CheckIcon sx={{ fontSize: 64, color: 'success.main', mb: 2 }} />

      <Typography variant="h6" gutterBottom>
        {t('profile.security.reset.passwordSent')}
      </Typography>

      <Typography variant="body2" color="text.secondary" sx={{ mb: 3 }}>
        {t('profile.security.reset.sentTo')}<br />
        <strong>{resetUserInfo?.email}</strong>
      </Typography>

      <Alert severity="success" sx={{ mb: 3 }}>
        <Typography variant="body2">
          <strong>{t('profile.security.reset.checkEmail')}</strong><br />
          {t('profile.security.reset.useTemporary')}
        </Typography>
      </Alert>

      <Paper sx={{ p: 2, bgcolor: 'grey.50' }}>
        <Typography variant="subtitle2" gutterBottom color="primary">
          {t('profile.security.reset.nextSteps')}:
        </Typography>
        <Typography variant="body2" component="div">
          1. {t('profile.security.reset.step1')}<br />
          2. {t('profile.security.reset.step2')}<br />
          3. {t('profile.security.reset.step3')}<br />
          4. {t('profile.security.reset.step4')}<br />
          5. {t('profile.security.reset.step5')}
        </Typography>
      </Paper>
    </Box>
  );

  // Componente para mostrar el estado de guardado automático
  const AutoSaveIndicator = () => {
  const { t } = useTranslation();
    if (!autoSaveStatus) return null;

    return (
      <Fade in={!!autoSaveStatus}>
        <Box
          sx={{
            position: 'fixed',
            top: 80,
            right: 24,
            zIndex: 1300,
            display: 'flex',
            alignItems: 'center',
            gap: 1,
            bgcolor: autoSaveStatus === 'saved' ? 'success.main' : autoSaveStatus === 'saving' ? 'info.main' : 'error.main',
            color: 'white',
            px: 2,
            py: 1,
            borderRadius: 2,
            boxShadow: 3,
          }}
        >
          {autoSaveStatus === 'saving' && <CircularProgress size={16} sx={{ color: 'white' }} />}
          {autoSaveStatus === 'saved' && <CheckIcon sx={{ fontSize: 20 }} />}
          {autoSaveStatus === 'error' && <WarningIcon sx={{ fontSize: 20 }} />}
          <Typography variant="body2" fontWeight={600}>
            {autoSaveMessage}
          </Typography>
        </Box>
      </Fade>
    );
  };

  // Render unit preferences tab
  // Render unit preferences tab
  const renderUnitPreferencesTab = () => (
    <Box sx={{ p: 4 }}>
      <Typography variant="h6" fontWeight={600} gutterBottom>
        {t('hardcoded.pages_profilepage.preferencias_de_unidades')}</Typography>
      <Typography variant="body2" color="text.secondary" paragraph>
        {t('hardcoded.pages_profilepage.configura_las_unidades_de_medida')}</Typography>

      {unitUpdateError && (
        <Alert severity="error" sx={{ mb: 3 }}>
          {unitUpdateError.message}
        </Alert>
      )}

      {unitPreferencesLoading ? (
        <Box sx={{ textAlign: 'center', py: 4 }}>
          <CircularProgress />
          <Typography variant="body2" sx={{ mt: 2 }}>
            {i18n.t('hardcoded.pages_profilepage.cargando_preferencias')}</Typography>
        </Box>
      ) : (
        <>
          {/* Unit Preferences Table */}
          <Box sx={{ mb: 3 }}>
            <TableContainer component={Paper}>
              <Table>
                <TableHead>
                  <TableRow sx={{
                    bgcolor: '#d32f2f',
                    '&:hover': {
                      bgcolor: '#b71c1c'
                    }
                  }}>
                    <TableCell sx={{
                      fontWeight: 600,
                      width: '45%',
                      color: 'white'
                    }}>
                      {t('hardcoded.pages_profilepage.tipo_de_medida')}</TableCell>
                    <TableCell align="center" sx={{
                      fontWeight: 600,
                      width: '35%',
                      color: 'white'
                    }}>
                      {t('hardcoded.components_alarmprofiles_profileform.unidad')}</TableCell>
                    <TableCell align="center" sx={{
                      fontWeight: 600,
                      width: '20%',
                      color: 'white'
                    }}>
                      {t('hardcoded.pages_profilepage.decimales_por_medida')}</TableCell>
                  </TableRow>
                </TableHead>
                <TableBody>
{/* Unidad y decimales son la misma decisión sobre la misma medida, así
                      que van en la misma fila. Antes los decimales vivían en una fila
                      aparte con los cuatro selectores juntos y había que adivinar cuál
                      correspondía a cuál. Las cuatro filas eran copias idénticas: se
                      recorren desde una lista para que añadir una medida sea una línea. */}
                  {[
                    {
                      unitType: 'pressure',
                      icon: <SpeedIcon color="action" />,
                      label: t('hardcoded.components_alarmprofiles_profilecard.presion'),
                      unitField: 'pressure_unit',
                      decimalsField: 'pressure_decimals'
                    },
                    {
                      unitType: 'temperature',
                      icon: <ThermostatIcon color="action" />,
                      label: t('hardcoded.components_alarmprofiles_profilecard.temperatura'),
                      unitField: 'temperature_unit',
                      decimalsField: 'temperature_decimals'
                    },
                    {
                      unitType: 'acceleration',
                      icon: <TrendingUpIcon color="action" />,
                      label: t('hardcoded.components_schematics_vehicletelemetrymodal copy.aceleracion'),
                      unitField: 'acceleration_unit',
                      decimalsField: 'acceleration_decimals'
                    },
                    {
                      unitType: 'speed',
                      icon: <TuneIcon color="action" />,
                      label: t('hardcoded.pages_profilepage.velocidad'),
                      unitField: 'speed_unit',
                      decimalsField: 'speed_decimals'
                    }
                  ].map((measure) => (
                    <TableRow key={measure.unitType}>
                      <TableCell>
                        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                          {measure.icon}
                          <Box>
                            <Typography variant="body2" fontWeight={600}>
                              {measure.label}
                            </Typography>
                            <Typography variant="caption" color="text.secondary">
                              {t('hardcoded.pages_profilepage.unidad_para_mostrar_valores_de_medida', {
                                medida: measure.label.toLowerCase()
                              })}
                            </Typography>
                          </Box>
                        </Box>
                      </TableCell>
                      <TableCell align="center">
                        <FormControl sx={{ minWidth: { xs: '100%', sm: 200 } }} size="small">
                          <Select
                            name={measure.unitField}
                            value={unitFormik.values[measure.unitField]}
                            onChange={unitFormik.handleChange}
                            error={unitFormik.touched[measure.unitField] && Boolean(unitFormik.errors[measure.unitField])}
                          >
                            {getUnitOptions(measure.unitType).map((unit) => (
                              <MenuItem key={unit.code} value={unit.code}>
                                {unit.name} ({unit.symbol})
                              </MenuItem>
                            ))}
                          </Select>
                        </FormControl>
                      </TableCell>
                      <TableCell align="center">
                        <FormControl sx={{ minWidth: { xs: '100%', sm: 110 } }} size="small">
                          <Select
                            name={measure.decimalsField}
                            value={unitFormik.values[measure.decimalsField]}
                            onChange={unitFormik.handleChange}
                            error={Boolean(unitFormik.touched[measure.decimalsField] && unitFormik.errors[measure.decimalsField])}
                          >
                            {[0, 1, 2, 3, 4, 5, 6].map((decimals) => (
                              <MenuItem key={`${measure.decimalsField}-${decimals}`} value={decimals.toString()}>
                                {decimals}
                              </MenuItem>
                            ))}
                          </Select>
                        </FormControl>
                      </TableCell>
                    </TableRow>
                  ))}

                </TableBody>
              </Table>
            </TableContainer>
          </Box>

          {/* Conversion Test Section */}
          <Card>
            <CardContent>
              <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 2 }}>
                <CalculateIcon color="primary" />
                <Typography variant="subtitle1" fontWeight={600}>
                  {t('hardcoded.pages_profilepage.prueba_de_conversion')}</Typography>
                <IconButton
                  size="small"
                  onClick={handleConversionTest}
                  sx={{ ml: 'auto' }}
                >
                  {showConversionTest ? <ExpandLessIcon /> : <ExpandMoreIcon />}
                </IconButton>
              </Box>

              <Collapse in={showConversionTest}>
                <Typography variant="body2" color="text.secondary" paragraph>
                  {t('hardcoded.pages_profilepage.prueba_las_conversiones_entre_diferentes')}</Typography>

                <Box component="form" onSubmit={conversionFormik.handleSubmit}>
                  <Grid container spacing={2} sx={{ mb: 2 }}>
                    <Grid item xs={12} sm={3}>
                      <TextField
                        fullWidth
                        label={t('hardcoded.components_admin_productform.valor')}
                        name="value"
                        type="number"
                        value={conversionFormik.values.value}
                        onChange={conversionFormik.handleChange}
                        size="small"
                      />
                    </Grid>
                    <Grid item xs={12} sm={2}>
                      <TextField
                        fullWidth
                        select
                        label={t('vehicles.form.type')}
                        name="conversion_type"
                        value={conversionFormik.values.conversion_type}
                        onChange={(e) => {
                          conversionFormik.setFieldValue('conversion_type', e.target.value);
                          const firstUnit = getUnitOptions(e.target.value)[0];
                          if (firstUnit) {
                            conversionFormik.setFieldValue('from_unit', firstUnit.code);
                            conversionFormik.setFieldValue('to_unit', firstUnit.code);
                          }
                        }}
                        size="small"
                        SelectProps={{ native: true }}
                      >
                        <option value="pressure">{t('hardcoded.components_alarmprofiles_profilecard.presion')}</option>
                        <option value="temperature">{i18n.t('hardcoded.components_alarmprofiles_profilecard.temperatura')}</option>
                        <option value="acceleration">{t('hardcoded.components_schematics_vehicletelemetrymodal copy.aceleracion')}</option>
                        <option value="speed">{t('hardcoded.pages_profilepage.velocidad')}</option>
                      </TextField>
                    </Grid>
                    <Grid item xs={12} sm={2}>
                      <TextField
                        fullWidth
                        select
                        label={t("hardcoded.pages_profilepage.de_label")}
                        name="from_unit"
                        value={conversionFormik.values.from_unit}
                        onChange={conversionFormik.handleChange}
                        size="small"
                        SelectProps={{ native: true }}
                      >
                        {getUnitOptions(conversionFormik.values.conversion_type).map((unit) => (
                          <option key={unit.code} value={unit.code}>
                            {unit.symbol}
                          </option>
                        ))}
                      </TextField>
                    </Grid>
                    <Grid item xs={12} sm={2}>
                      <TextField
                        fullWidth
                        select
                        label={t("hardcoded.pages_profilepage.a_label")}
                        name="to_unit"
                        value={conversionFormik.values.to_unit}
                        onChange={conversionFormik.handleChange}
                        size="small"
                        SelectProps={{ native: true }}
                      >
                        {getUnitOptions(conversionFormik.values.conversion_type).map((unit) => (
                          <option key={unit.code} value={unit.code}>
                            {unit.symbol}
                          </option>
                        ))}
                      </TextField>
                    </Grid>
                    <Grid item xs={12} sm={3}>
                      <Button
                        type="submit"
                        variant="contained"
                        size="small"
                        disabled={conversionTestLoading}
                        fullWidth
                        sx={{ height: '40px' }}
                      >
                        {conversionTestLoading ? <CircularProgress size={20} /> : t('hardcoded.pages_profilepage.convertir')}
                      </Button>
                    </Grid>
                  </Grid>

                  {conversionResult && (
                    <Alert severity="success" sx={{ mb: 2 }}>
                      <Typography variant="body2">
                        <strong>{i18n.t('hardcoded.pages_profilepage.resultado')}</strong> {conversionResult.original_value} {conversionResult.from_unit} = {' '}
                        <strong>{conversionResult.converted_value}</strong> {conversionResult.to_unit}
                      </Typography>
                    </Alert>
                  )}

                  {conversionError && (
                    <Alert severity="error" sx={{ mb: 2 }}>
                      {conversionError}
                    </Alert>
                  )}
                </Box>

                {/* Conversion Examples */}
                {conversionExamples && !conversionExamplesLoading && (
                  <Paper sx={{ p: 2, bgcolor: 'grey.50' }}>
                    <Typography variant="subtitle2" gutterBottom>
                      {t('hardcoded.pages_profilepage.ejemplos_de_conversion')}</Typography>
                    <Grid container spacing={2}>
                      {Object.entries(conversionExamples.examples).map(([type, examples]) => (
                        <Grid item xs={12} sm={6} md={3} key={type}>
                          <Typography variant="caption" color="primary" gutterBottom display="block">
                            {unitPreferencesHelpers.getUnitTypeDisplayName(type)}
                          </Typography>
                          {examples.slice(0, 2).map((example, index) => (
                            <Typography key={index} variant="caption" display="block" sx={{ fontSize: '0.75rem' }}>
                              {example.value} {example.from} = {example.result} {example.to}
                            </Typography>
                          ))}
                        </Grid>
                      ))}
                    </Grid>
                  </Paper>
                )}
              </Collapse>
            </CardContent>
          </Card>
        </>
      )}
    </Box>
  );

  if (profileLoading) {
    return <LoadingPage message={t('profile.loading')} />;
  }

  if (profileError) {
    return (
      <Container>
        <Paper sx={{ p: 4, textAlign: 'center' }}>
          <Typography variant="h5" color="error" gutterBottom>
            {t('profile.errorLoadingProfile')}
          </Typography>
          <Typography variant="body1" color="text.secondary" paragraph>
            {profileError.message}
          </Typography>
          <Button variant="contained" onClick={refetchProfile}>
            {t('profile.tryAgain')}
          </Button>
        </Paper>
      </Container>
    );
  }

  const availableLanguages = getAvailableLanguages();
  const resetSteps = [
    t('profile.security.reset.steps.enterEmail'),
    t('profile.security.reset.steps.confirmSend'),
    t('profile.security.reset.steps.completed')
  ];

  return (
    <Container maxWidth={false} disableGutters>
      {/* Indicador de guardado automático */}
      <AutoSaveIndicator />

      {/* Header */}
      <Box sx={{ mb: 4 }}>
        <Typography variant="h4" fontWeight={700} gutterBottom>
          {t('profile.title')}
        </Typography>
      </Box>

      {/* Success Message */}
      {successMessage && (
        <Alert severity="success" sx={{ mb: 3 }}>
          {successMessage}
        </Alert>
      )}

      {/* Timezone Loading Error */}
      {timezonesError && (
        <Alert severity="warning" sx={{ mb: 3 }}>
          {t('profile.timezoneLoadingError', {
            defaultValue: 'Error al cargar zonas horarias. Se usarán valores por defecto.'
          })}
        </Alert>
      )}

      {/* Profile Header Card */}
      <Card sx={{ mb: 3 }}>
        <CardContent sx={{ p: 4 }}>
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 3 }}>
            <Avatar
              sx={{
                width: 100,
                height: 100,
                bgcolor: 'primary.main',
                fontSize: '2rem',
                fontWeight: 700,
              }}
            >
              {userProfile?.name?.charAt(0)?.toUpperCase()}
            </Avatar>
            <Box sx={{ flex: 1 }}>
              <Typography variant="h5" fontWeight={600}>
                {userProfile?.name} {userProfile?.last_name}
              </Typography>
              <Typography variant="body1" color="text.secondary" gutterBottom>
                {userProfile?.email}
              </Typography>
              <Box sx={{ display: 'flex', gap: 1, flexWrap: 'wrap' }}>
                <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
                  <BusinessIcon fontSize="small" color="action" />
                  <Typography variant="caption">
                    {userProfile?.company_name}
                  </Typography>
                </Box>
              </Box>
            </Box>
          </Box>
        </CardContent>
      </Card>

      {/* Tabs */}
      <Paper sx={{ mb: 3 }}>
        <Tabs
          value={tabValue}
          onChange={handleTabChange}
          variant={isMobile ? 'scrollable' : 'fullWidth'}
          scrollButtons={isMobile ? 'auto' : false}
          allowScrollButtonsMobile
          sx={{ borderBottom: 1, borderColor: 'divider' }}
        >
          <Tab icon={<PersonIcon />} label={t('profile.tabs.personalInfo')} />
          <Tab icon={<LockIcon />} label={t('profile.tabs.security')} />
          <Tab icon={<SettingsIcon />} label={t('hardcoded.pages_profilepage.preferencias_de_unidades')} />
          <Tab icon={<NotificationsIcon />} label={t('navbar.notifications')} />
        </Tabs>
      </Paper>

      {/* Tab Content */}
      <Paper>
        {/* Personal Information Tab - Con guardado automático */}
        {tabValue === 0 && (
          <Box sx={{ p: 4 }}>
            {updateError && (
              <Alert severity="error" sx={{ mb: 3 }}>
                {updateError.message}
              </Alert>
            )}

            <Box>
              <Grid container spacing={3}>
                <Grid item xs={12} sm={6}>
                  <TextField
                    fullWidth
                    label={t('profile.personalInfo.firstName')}
                    name="name"
                    value={profileFormik.values.name}
                    onChange={profileFormik.handleChange}
                    onBlur={profileFormik.handleBlur}
                    error={profileFormik.touched.name && Boolean(profileFormik.errors.name)}
                    helperText={profileFormik.touched.name && profileFormik.errors.name}
                    InputProps={{
                      startAdornment: <PersonIcon color="action" sx={{ mr: 1 }} />,
                    }}
                  />
                </Grid>

                <Grid item xs={12} sm={6}>
                  <TextField
                    fullWidth
                    label={t('profile.personalInfo.lastName')}
                    name="last_name"
                    value={profileFormik.values.last_name}
                    onChange={profileFormik.handleChange}
                    onBlur={profileFormik.handleBlur}
                    error={profileFormik.touched.last_name && Boolean(profileFormik.errors.last_name)}
                    helperText={profileFormik.touched.last_name && profileFormik.errors.last_name}
                    InputProps={{
                      startAdornment: <PersonIcon color="action" sx={{ mr: 1 }} />,
                    }}
                  />
                </Grid>

                <Grid item xs={12}>
                  <TextField
                    fullWidth
                    label={t('profile.personalInfo.emailAddress')}
                    name="email"
                    type="email"
                    value={profileFormik.values.email}
                    onChange={profileFormik.handleChange}
                    onBlur={profileFormik.handleBlur}
                    error={profileFormik.touched.email && Boolean(profileFormik.errors.email)}
                    helperText={profileFormik.touched.email && profileFormik.errors.email}
                    InputProps={{
                      startAdornment: <EmailIcon color="action" sx={{ mr: 1 }} />,
                    }}
                  />
                </Grid>

                <Grid item xs={12} sm={6}>
                  <PhoneInput
                    value={profileFormik.values.phone}
                    onChange={(value) => profileFormik.setFieldValue('phone', value)}
                    error={profileFormik.touched.phone && Boolean(profileFormik.errors.phone)}
                    helperText={profileFormik.touched.phone && profileFormik.errors.phone}
                    label={t('profile.personalInfo.phoneNumber')}
                  />
                </Grid>

                <Grid item xs={12} sm={6}>
                  <FormControl
                    fullWidth
                    error={profileFormik.touched.language && Boolean(profileFormik.errors.language)}
                    disabled={isChangingLanguage}
                  >
                    <InputLabel id="profile-language-label">{t('profile.personalInfo.language')}</InputLabel>
                    <Select
                      labelId="profile-language-label"
                      label={t('profile.personalInfo.language')}
                      name="language"
                      value={profileFormik.values.language}
                      onChange={handleLanguageChange}
                      onBlur={profileFormik.handleBlur}
                    >
                      {availableLanguages.map((language) => (
                        <MenuItem key={language.code} value={language.code}>
                          <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                            <Typography component="span" sx={{ fontSize: '1rem', lineHeight: 1 }}>
                              {language.flag}
                            </Typography>
                            <Typography component="span">
                              {language.label} ({language.nativeLabel})
                            </Typography>
                          </Box>
                        </MenuItem>
                      ))}
                    </Select>
                    {profileFormik.touched.language && profileFormik.errors.language && (
                      <Typography variant="caption" color="error" sx={{ mt: 0.5, ml: 1.75 }}>
                        {profileFormik.errors.language}
                      </Typography>
                    )}
                  </FormControl>
                </Grid>

                <Grid item xs={12}>
                  <Autocomplete
                    fullWidth
                    value={getSelectedTimezone()}
                    onChange={handleTimezoneChange}
                    options={timezones}
                    getOptionLabel={(option) => option ? option.label : ''}
                    isOptionEqualToValue={(option, value) => option.value === value.value}
                    loading={timezonesLoading}
                    disabled={isChangingLanguage}
                    renderInput={(params) => (
                      <TextField
                        {...params}
                        label={t('profile.personalInfo.timezone', {
                          defaultValue: 'Zona Horaria'
                        })}
                        error={profileFormik.touched.timezone && Boolean(profileFormik.errors.timezone)}
                        helperText={profileFormik.touched.timezone && profileFormik.errors.timezone}
                        InputProps={{
                          ...params.InputProps,
                          startAdornment: (
                            <>
                              <TimezoneIcon color="action" sx={{ mr: 1 }} />
                              {params.InputProps.startAdornment}
                            </>
                          ),
                          endAdornment: (
                            <>
                              {timezonesLoading ? (
                                <CircularProgress color="inherit" size={20} />
                              ) : null}
                              {params.InputProps.endAdornment}
                            </>
                          ),
                        }}
                      />
                    )}
                    renderOption={(props, option) => (
                      <Box component="li" {...props}>
                        <Box>
                          <Typography variant="body2">
                            {option.label}
                          </Typography>
                        </Box>
                      </Box>
                    )}
                    filterOptions={(options, { inputValue }) => {
                      const filterValue = inputValue.toLowerCase();
                      return options.filter(option =>
                        option.label.toLowerCase().includes(filterValue) ||
                        option.value.toLowerCase().includes(filterValue) ||
                        option.abbr.toLowerCase().includes(filterValue)
                      );
                    }}
                  />
                </Grid>
              </Grid>
            </Box>
          </Box>
        )}

        {/* Security Tab */}
        {tabValue === 1 && (
          <Box sx={{ p: 4 }}>
            <Typography variant="h6" fontWeight={600} gutterBottom>
              {t('profile.security.changePassword')}
            </Typography>
            <Typography variant="body2" color="text.secondary" paragraph>
              {t('profile.security.passwordSubtitle')}
            </Typography>

            {passwordError && (
              <Alert severity="error" sx={{ mb: 3 }}>
                {passwordError.message}
              </Alert>
            )}

            <Box component="form" onSubmit={passwordFormik.handleSubmit}>
              <Grid container spacing={3}>
                <Grid item xs={12}>
                  <TextField
                    fullWidth
                    label={t('profile.security.currentPassword')}
                    name="current_password"
                    type="password"
                    value={passwordFormik.values.current_password}
                    onChange={passwordFormik.handleChange}
                    onBlur={passwordFormik.handleBlur}
                    error={passwordFormik.touched.current_password && Boolean(passwordFormik.errors.current_password)}
                    helperText={passwordFormik.touched.current_password && passwordFormik.errors.current_password}
                  />
                </Grid>

                <Grid item xs={12}>
                  <TextField
                    fullWidth
                    label={t('profile.security.newPassword')}
                    name="new_password"
                    type="password"
                    value={passwordFormik.values.new_password}
                    onChange={passwordFormik.handleChange}
                    onBlur={passwordFormik.handleBlur}
                    error={passwordFormik.touched.new_password && Boolean(passwordFormik.errors.new_password)}
                    helperText={passwordFormik.touched.new_password && passwordFormik.errors.new_password}
                  />
                </Grid>

                <Grid item xs={12}>
                  <TextField
                    fullWidth
                    label={t('profile.security.confirmNewPassword')}
                    name="confirm_password"
                    type="password"
                    value={passwordFormik.values.confirm_password}
                    onChange={passwordFormik.handleChange}
                    onBlur={passwordFormik.handleBlur}
                    error={passwordFormik.touched.confirm_password && Boolean(passwordFormik.errors.confirm_password)}
                    helperText={passwordFormik.touched.confirm_password && passwordFormik.errors.confirm_password}
                  />
                </Grid>

                <Grid item xs={12}>
                  <Button
                    type="submit"
                    variant="contained"
                    startIcon={<LockIcon />}
                    disabled={passwordLoading}
                    sx={{ mr: 2 }}
                  >
                    {passwordLoading ? t('profile.security.updating') : t('profile.security.updatePassword')}
                  </Button>

                  <Button
                    variant="text"
                    startIcon={<KeyIcon />}
                    onClick={handleOpenResetDialog}
                    disabled={passwordLoading}
                    sx={{ textTransform: 'none' }}
                  >
                    {t('profile.security.reset.forgotPassword')}
                  </Button>
                </Grid>
              </Grid>
            </Box>

            <Divider sx={{ my: 4 }} />

            <Typography variant="h6" fontWeight={600} gutterBottom>
              {t('profile.security.accountInfo')}
            </Typography>
            <Grid container spacing={2}>
              <Grid item xs={12} sm={6}>
                <Typography variant="body2" color="text.secondary">
                  {t('profile.security.accountCreated')}
                </Typography>
                <Typography variant="body1">
                  {userProfile?.created_at ? new Date(userProfile.created_at).toLocaleDateString() : t('hardcoded.pages_profilepage.na')}
                </Typography>
              </Grid>
              <Grid item xs={12} sm={6}>
                <Typography variant="body2" color="text.secondary">
                  {t('profile.security.lastUpdated')}
                </Typography>
                <Typography variant="body1">
                  {userProfile?.updated_at ? new Date(userProfile.updated_at).toLocaleDateString() : t('hardcoded.pages_profilepage.na')}
                </Typography>
              </Grid>
            </Grid>
          </Box>
        )}

        {/* Unit Preferences Tab */}
        {tabValue === 2 && renderUnitPreferencesTab()}

        {tabValue === 3 && (
          <Box sx={{ p: 4 }}>
            <AdoptionNotificationPreferences />
            <NotificationPreferences />
          </Box>
        )}
      </Paper>

      {/* Password Reset Dialog */}
      <Dialog
        open={resetDialogOpen}
        onClose={handleCloseResetDialog}
        maxWidth="sm"
        fullWidth
        fullScreen={isMobile}
        PaperProps={{
          sx: { borderRadius: { xs: 0, sm: 2 } }
        }}
      >
        <DialogTitle>
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
            <KeyIcon color="primary" />
            {t('profile.security.reset.resetPassword')}
          </Box>
        </DialogTitle>

        <DialogContent>
          <Stepper activeStep={resetStep} sx={{ mb: 3 }}>
            {resetSteps.map((label) => (
              <Step key={label}>
                <StepLabel>{label}</StepLabel>
              </Step>
            ))}
          </Stepper>

          {resetStep === 0 && renderResetEmailStep()}
          {resetStep === 1 && renderResetConfirmationStep()}
          {resetStep === 2 && renderResetSuccessStep()}
        </DialogContent>

        <DialogActions sx={{ p: 3, pt: 0 }}>
          {resetStep === 0 && (
            <>
              <Button onClick={handleCloseResetDialog} disabled={resetLoading}>
                {t('common.cancel')}
              </Button>
              <Button
                onClick={resetFormik.handleSubmit}
                variant="contained"
                disabled={resetLoading}
                startIcon={resetLoading ? <CircularProgress size={20} /> : <EmailIcon />}
              >
                {resetLoading ? t('profile.security.reset.validating') : t('profile.security.reset.validateEmail')}
              </Button>
            </>
          )}

          {resetStep === 1 && (
            <>
              <Button onClick={() => setResetStep(0)} disabled={resetLoading}>
                {t('profile.security.reset.changeEmail')}
              </Button>
              <Button
                onClick={handlePasswordReset}
                variant="contained"
                disabled={resetLoading}
                startIcon={resetLoading ? <CircularProgress size={20} /> : <SendIcon />}
              >
                {resetLoading ? t('profile.security.reset.sending') : t('profile.security.reset.sendNewPassword')}
              </Button>
            </>
          )}

          {resetStep === 2 && (
            <>
              <Button
                onClick={handleCloseResetDialog}
                variant="outlined"
              >
                {t('common.close')}
              </Button>
              <Button
                onClick={() => {
                  handleCloseResetDialog();
                  window.location.reload();
                }}
                variant="contained"
              >
                {t('profile.security.reset.refreshPage')}
              </Button>
            </>
          )}
        </DialogActions>
      </Dialog>
    </Container>
  );
};

export default ProfilePage;